import React, { memo } from 'react'

import {
  Divider,
  Button,
  Icon,
  Toggle,
  Trash,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, func, bool, number, oneOfType } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import InfoIconTooltip from 'Pods/ThreeDStudio/Shared/InfoIconTooltip/InfoIconTooltip'
import PaidFeatureBadge from 'Pods/ThreeDStudio/Shared/PaidFeatureBadge/PaidFeatureBadge'
import SubscribersOnlyHover from 'Pods/ThreeDStudio/Shared/SubscribersOnlyHover/SubscribersOnlyHover'

import { NOOP } from 'Utils/stableDefaults'

const deleteIcon = <Icon svgComponent={Trash} color="dark" size={iconSize} />
const infoIconSize = { height: '14px', width: '14px' }

const formatCreatedAt = (isoString) =>
  new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

function ModelCardContent({
  title = '',
  createdAt,
  onDelete: handleDelete = NOOP,
  isDeleting = false,
  runId = undefined,
  isPrivate = false,
  canTogglePrivacy = false,
  onTogglePrivacy: handleTogglePrivacy = NOOP,
  isPrivacyUpdating = false,
}) {
  const t = useTranslation()

  const privacyRow = (
    <div className="model-card__privacy">
      <div className="model-card__privacy-label">
        {/* The control is the input rendered inside the Triangle UI Toggle with this id */}
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor={`make-private-${runId}`} className="model-card__privacy-label-text cgt-body-base-semibold">
          {t('sidebar.modelCard.makePrivate')}
        </label>
        <InfoIconTooltip
          id={`make-private-info-${runId}`}
          content={t('shared.privateMode.tooltip')}
          label={t('shared.privateMode.label')}
          tooltipClassName="model-settings__tooltip"
          triggerClassName="model-card__privacy-info-trigger"
          iconSize={infoIconSize}
          place="top"
        />
      </div>
      <div className="model-card__privacy-control">
        {!canTogglePrivacy && <PaidFeatureBadge label={t('shared.subscribersOnly.title')} />}
        <Toggle
          id={`make-private-${runId}`}
          className="model-card__privacy-toggle"
          size="small"
          isChecked={isPrivate}
          onChange={handleTogglePrivacy}
          disabled={!canTogglePrivacy || isPrivacyUpdating}
        />
      </div>
    </div>
  )

  return (
    <div className="model-card__content">
      <div className="model-card__content-header">
        {title && <span className="cgt-body-base-semibold">{title}</span>}
        <span className="model-card__content-created-at cgt-caption">
          {t('sidebar.modelCard.created', { date: formatCreatedAt(createdAt) })}
        </span>
      </div>
      <Divider />
      <Button
        type="ghost"
        variant="with-icon"
        icon={deleteIcon}
        size="small"
        customClass="model-card__content-button"
        onClick={handleDelete}
        disabled={isDeleting}
      >
        {t('sidebar.modelCard.delete')}
      </Button>
      <Divider />
      {canTogglePrivacy
        ? privacyRow
        : (
          <SubscribersOnlyHover
            id={`make-private-upsell-${runId}`}
            placement="right"
            alignBottom
            containerClassName="model-card__privacy-hover"
          >
            {privacyRow}
          </SubscribersOnlyHover>
        )}
    </div>
  )
}

ModelCardContent.propTypes = {
  title: string,
  createdAt: string.isRequired,
  onDelete: func,
  isDeleting: bool,
  runId: oneOfType([string, number]),
  isPrivate: bool,
  canTogglePrivacy: bool,
  onTogglePrivacy: func,
  isPrivacyUpdating: bool,
}

export default memo(ModelCardContent)
